import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, Users, Share2, Gift, Heart, Star, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { ReferralShareDialog } from '@/components/sharing/ReferralShareDialog';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const TIERS = [
  { required: 1, reward: 1, cumulative: 1 },
  { required: 5, reward: 3, cumulative: 4 },
  { required: 12, reward: 6, cumulative: 10 },
  { required: 20, reward: 12, cumulative: 22 },
];
const MAX_REWARD = 20;

function getTierProgress(paidCount: number) {
  let earned = 0;
  let currentTierIndex = 0;
  for (let i = 0; i < TIERS.length; i++) {
    if (paidCount >= TIERS[i].required) {
      earned += TIERS[i].reward;
      currentTierIndex = i + 1;
    }
  }
  earned = Math.min(earned, MAX_REWARD);

  const nextTier = TIERS[currentTierIndex] || null;
  const prevRequired = currentTierIndex > 0 ? TIERS[currentTierIndex - 1].required : 0;
  const progressPercent = nextTier
    ? ((paidCount - prevRequired) / (nextTier.required - prevRequired)) * 100
    : 100;

  return { earned, nextTier, progressPercent: Math.min(Math.max(progressPercent, 0), 100), currentTierIndex };
}

const copy = {
  en: {
    title: 'Share the Words',
    tagline: 'Help more people say what matters — and earn free announcements when they do.',
    paidReferrals: 'Paid referrals',
    earned: 'Announcements earned',
    nextUnlock: '{n} more referral(s) unlock {r} additional announcements.',
    maxReached: 'You\'ve earned the maximum reward! Thank you for sharing.',
    copyLink: 'Copy Link',
    copied: 'Copied!',
    share: 'Share',
    viewDetails: 'View full referral details',
    linkCopied: 'Referral link copied!',
  },
  es: {
    title: 'Comparte las Palabras',
    tagline: 'Ayuda a más personas a decir lo que importa — y gana anuncios gratis.',
    paidReferrals: 'Referencias pagadas',
    earned: 'Anuncios ganados',
    nextUnlock: '{n} referencia(s) más desbloquean {r} anuncios adicionales.',
    maxReached: '¡Has ganado la recompensa máxima! Gracias por compartir.',
    copyLink: 'Copiar enlace',
    copied: '¡Copiado!',
    share: 'Compartir',
    viewDetails: 'Ver detalles completos',
    linkCopied: '¡Enlace de referencia copiado!',
  },
  fr: {
    title: 'Partagez les Mots',
    tagline: 'Aidez plus de gens à dire ce qui compte — et gagnez des annonces gratuites.',
    paidReferrals: 'Parrainages payés',
    earned: 'Annonces gagnées',
    nextUnlock: '{n} parrainage(s) de plus débloquent {r} annonces supplémentaires.',
    maxReached: 'Vous avez atteint la récompense maximale ! Merci de partager.',
    copyLink: 'Copier le lien',
    copied: 'Copié !',
    share: 'Partager',
    viewDetails: 'Voir tous les détails',
    linkCopied: 'Lien de parrainage copié !',
  },
  pt: {
    title: 'Compartilhe as Palavras',
    tagline: 'Ajude mais pessoas a dizer o que importa — e ganhe anúncios grátis.',
    paidReferrals: 'Indicações pagas',
    earned: 'Anúncios ganhos',
    nextUnlock: '{n} indicação(ões) a mais desbloqueiam {r} anúncios adicionais.',
    maxReached: 'Você ganhou a recompensa máxima! Obrigado por compartilhar.',
    copyLink: 'Copiar link',
    copied: 'Copiado!',
    share: 'Compartilhar',
    viewDetails: 'Ver detalhes completos',
    linkCopied: 'Link de indicação copiado!',
  },
  ar: { title: 'شارك الكلمات', tagline: 'ساعد المزيد على قول ما يهم — واكسب إعلانات مجانية.', paidReferrals: 'إحالات مدفوعة', earned: 'إعلانات مكتسبة', nextUnlock: '{n} إحالة(ات) أخرى تفتح {r} إعلانات إضافية.', maxReached: 'لقد حصلت على الحد الأقصى! شكرًا للمشاركة.', copyLink: 'نسخ الرابط', copied: 'تم النسخ!', share: 'مشاركة', viewDetails: 'عرض التفاصيل الكاملة', linkCopied: 'تم نسخ رابط الإحالة!' },
  ja: { title: '言葉を共有', tagline: 'もっと多くの人が大切なことを伝えられるように — 無料アナウンスメントを獲得。', paidReferrals: '有料紹介', earned: '獲得アナウンスメント', nextUnlock: 'あと{n}件の紹介で{r}件の追加アナウンスメントをアンロック。', maxReached: '最大報酬を獲得しました！共有ありがとうございます。', copyLink: 'リンクをコピー', copied: 'コピー済み！', share: '共有', viewDetails: '詳細を見る', linkCopied: '紹介リンクをコピーしました！' },
  ko: { title: '말을 나누세요', tagline: '더 많은 사람들이 중요한 말을 할 수 있도록 — 무료 어나운스먼트를 획득하세요.', paidReferrals: '유료 추천', earned: '획득한 어나운스먼트', nextUnlock: '{n}개의 추천이 더 있으면 {r}개의 추가 어나운스먼트를 잠금 해제합니다.', maxReached: '최대 보상을 획득했습니다! 공유해 주셔서 감사합니다.', copyLink: '링크 복사', copied: '복사됨!', share: '공유', viewDetails: '전체 세부 정보 보기', linkCopied: '추천 링크가 복사되었습니다!' },
  id: { title: 'Bagikan Kata-Kata', tagline: 'Bantu lebih banyak orang mengatakan yang penting — dan dapatkan pengumuman gratis.', paidReferrals: 'Referensi berbayar', earned: 'Pengumuman diperoleh', nextUnlock: '{n} referensi lagi membuka {r} pengumuman tambahan.', maxReached: 'Anda telah mendapatkan hadiah maksimum! Terima kasih telah berbagi.', copyLink: 'Salin tautan', copied: 'Disalin!', share: 'Bagikan', viewDetails: 'Lihat detail lengkap', linkCopied: 'Tautan referensi disalin!' },

  hi: {
    title: 'शब्दों को शेयर करें',
    tagline: 'अधिक लोगों को यह कहने में मदद करें कि क्या मायने रखता है — और जब वे ऐसा करते हैं तो मुफ्त घोषणाएं अर्जित करें।',
    paidReferrals: 'भुगतान किए गए रेफरल',
    earned: 'अर्जित घोषणाएं',
    nextUnlock: '{n} और रेफरल {r} अतिरिक्त घोषणाओं को अनलॉक करते हैं।',
    maxReached: 'आपने अधिकतम इनाम अर्जित किया है! शेयर करने के लिए धन्यवाद।',
    copyLink: 'लिंक कॉपी करें',
    copied: 'कॉपी किया गया!',
    share: 'शेयर करें',
    viewDetails: 'पूरे रेफरल विवरण देखें',
    linkCopied: 'रेफरल लिंक कॉपी किया गया!',
  },
};

const tierIcons = [Heart, Gift, Star, Sparkles];

export function ReferralProgressPanel() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const c = lc(copy, language);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [paidCount, setPaidCount] = useState(0);
  const [earnedFromRewards, setEarnedFromRewards] = useState(0);
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      const [{ data: profile }, { data: referrals }, { data: rewards }] = await Promise.all([
        supabase.from('profiles').select('referral_code').eq('user_id', user.id).single(),
        supabase.from('referrals').select('id, status, qualified_at').eq('referrer_id', user.id),
        supabase.from('referral_rewards').select('tier_name, reward_credits').eq('user_id', user.id),
      ]);
      if (profile) setReferralCode((profile as any).referral_code);
      if (referrals) {
        setPaidCount(referrals.filter((r: any) => r.status === 'qualified' || r.status === 'completed').length);
      }
      if (rewards) {
        setEarnedFromRewards((rewards as any[]).reduce((sum: number, r: any) => sum + r.reward_credits, 0));
      }
    };
    fetchData();
  }, [user]);

  const referralLink = referralCode ? `${window.location.origin}/?ref=${referralCode}` : '';
  const { earned: calculatedEarned, nextTier, progressPercent, currentTierIndex } = getTierProgress(paidCount);
  const earned = earnedFromRewards > 0 ? Math.min(earnedFromRewards, MAX_REWARD) : calculatedEarned;

  const handleCopy = async () => {
    if (!referralLink) return;
    try { await navigator.clipboard.writeText(referralLink); } catch {
      const ta = document.createElement('textarea');
      ta.value = referralLink; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success(c.linkCopied);
    setTimeout(() => setCopied(false), 2500);
  };

  if (!referralCode) return null;

  return (
    <>
      <Card className="glass border-0 rounded-2xl overflow-hidden">
        <CardContent className="p-5 md:p-6">
          {/* Header */}
          <div className="flex items-center gap-2 mb-1">
            <Users className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-bold text-foreground">{c.title}</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">{c.tagline}</p>

          {/* Stats Row */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-xl bg-muted/40 p-3 text-center">
              <p className="text-2xl font-bold text-foreground">{paidCount}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{c.paidReferrals}</p>
            </div>
            <div className="rounded-xl bg-primary/10 p-3 text-center">
              <p className="text-2xl font-bold text-primary">{earned}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{c.earned}</p>
            </div>
          </div>

          {/* Tier Progress */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              {TIERS.map((tier, i) => {
                const Icon = tierIcons[i];
                const reached = paidCount >= tier.required;
                return (
                  <div key={tier.required} className="flex flex-col items-center gap-1">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${reached ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <span className={`text-[9px] font-semibold ${reached ? 'text-primary' : 'text-muted-foreground'}`}>
                      {tier.required}
                    </span>
                  </div>
                );
              })}
            </div>
            <Progress value={progressPercent} className="h-2 rounded-full" />
            <p className="text-xs text-muted-foreground mt-2">
              {nextTier
                ? c.nextUnlock
                    .replace('{n}', String(nextTier.required - paidCount))
                    .replace('{r}', String(nextTier.reward))
                : c.maxReached}
            </p>
          </div>

          {/* Link + Actions */}
          <div className="flex gap-2 mb-3">
            <Input
              readOnly
              value={referralLink}
              className="rounded-xl bg-muted/50 text-xs font-mono h-9"
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <Button size="sm" variant="outline" className="rounded-xl shrink-0 h-9 gap-1.5" onClick={handleCopy}>
              {copied ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? c.copied : c.copyLink}
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <Link to="/refer" className="text-xs text-primary hover:underline">
              {c.viewDetails} →
            </Link>
            <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => setShareOpen(true)}>
              <Share2 className="h-3.5 w-3.5" />
              {c.share}
            </Button>
          </div>
        </CardContent>
      </Card>

      <ReferralShareDialog open={shareOpen} onOpenChange={setShareOpen} referralLink={referralLink} />
    </>
  );
}
